import Axios from 'axios';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Store } from '../Store';
import { getError } from '../utils';
import { api } from '../Api';

/**
 * SHOPPING BALANCE LOG — every movement of Shopping Balance, from every source.
 *
 *   /shopping-balance-log          the signed-in member's own history
 *   /admin/shopping-balance-log    everyone's (admin), or one member's
 *                                  (?username= / ?userId=)
 *
 * The server (GET /api/shopping-log/mine | /admin) unions the ledger with the
 * transfer, request-money, convert, point, OTP and legacy order collections,
 * so this screen only filters, totals and shows. Shopping Balance is in Tk;
 * every amount is also shown in points at 1 pt = Tk 12.
 */

const DEFAULT_TK_PER_POINT = 12;

const TYPE_ICON = {
  topup: 'fa-plus-circle',
  self_topup: 'fa-retweet',
  topup_undo: 'fa-undo',
  supervisor_share_in: 'fa-arrow-down',
  supervisor_share_out: 'fa-arrow-up',
  association_in: 'fa-arrow-down',
  association_out: 'fa-arrow-up',
  money_request_in: 'fa-hand-o-down',
  money_request_out: 'fa-hand-o-up',
  merchant_withdraw: 'fa-money',
  order_payment: 'fa-shopping-cart',
  order_refund: 'fa-reply',
  convert: 'fa-exchange',
  admin_send_in: 'fa-building',
  admin_send_out: 'fa-paper-plane',
  admin_deduct: 'fa-minus-circle',
  admin_deduct_return: 'fa-reply',
  transfer_in: 'fa-arrow-down',
  transfer_out: 'fa-arrow-up',
  otp_charge: 'fa-comment',
  order_commission_legacy: 'fa-history',
  sales_generation_legacy: 'fa-history',
  other: 'fa-circle-o',
};

const SORTS = [
  { value: 'newest', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'largest', label: 'Largest amount' },
  { value: 'amount_desc', label: 'Biggest credit first' },
  { value: 'amount_asc', label: 'Biggest debit first' },
];

const EMPTY_FILTERS = {
  q: '',
  username: '',
  from: '',
  to: '',
  types: [],
  direction: '',
  min: '',
  max: '',
  unit: 'tk',
  sort: 'newest',
};

const fmt = (n, digits = 2) =>
  Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: digits });
const tk = (n) => `Tk ${fmt(n)}`;
const dash = <span className="sbl-dash">—</span>;

const pad = (n) => String(n).padStart(2, '0');
const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const fmtDateTime = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.valueOf())) return { date: '—', time: '' };
  return {
    date: d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
  };
};

const QUICK_RANGES = [
  { key: 'today', label: 'Today', days: 0 },
  { key: '7', label: '7 days', days: 6 },
  { key: '30', label: '30 days', days: 29 },
  { key: '90', label: '90 days', days: 89 },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' },
];

const rangeFor = (key) => {
  const now = new Date();
  if (key === 'all') return { from: '', to: '' };
  if (key === 'month') return { from: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), to: isoDay(now) };
  const q = QUICK_RANGES.find((r) => r.key === key);
  const start = new Date(now);
  start.setDate(start.getDate() - (q?.days || 0));
  return { from: isoDay(start), to: isoDay(now) };
};

const csvCell = (v) => {
  const s = v === null || v === undefined ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export default function ShoppingBalanceLogScreen({ admin = false }) {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const navigate = useNavigate();
  const [urlQuery, setUrlQuery] = useSearchParams();
  const auth = useMemo(() => ({ headers: { authorization: `Bearer ${userInfo?.token}` } }), [userInfo]);

  const initial = useMemo(
    () => ({
      ...EMPTY_FILTERS,
      username: admin ? urlQuery.get('username') || '' : '',
      from: urlQuery.get('from') || '',
      to: urlQuery.get('to') || '',
      types: (urlQuery.get('type') || '').split(',').filter(Boolean),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const [userId, setUserId] = useState(admin ? urlQuery.get('userId') || '' : '');
  const [filters, setFilters] = useState(initial);
  const [applied, setApplied] = useState(initial);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [open, setOpen] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const tkPerPoint = data?.tkPerPoint || DEFAULT_TK_PER_POINT;
  const pt = (n) => `${fmt((Number(n) || 0) / tkPerPoint)} pt`;

  const buildParams = useCallback(
    (extra = {}) => {
      const p = new URLSearchParams();
      const f = applied;
      if (f.q) p.set('q', f.q);
      if (admin && userId) p.set('userId', userId);
      else if (admin && f.username) p.set('username', f.username);
      if (f.from) p.set('from', f.from);
      if (f.to) p.set('to', f.to);
      if (f.types.length) p.set('type', f.types.join(','));
      if (f.direction) p.set('direction', f.direction);
      if (f.min !== '') p.set('min', f.min);
      if (f.max !== '') p.set('max', f.max);
      if (f.unit === 'pt') p.set('unit', 'pt');
      p.set('sort', f.sort);
      p.set('page', String(page));
      p.set('pageSize', String(pageSize));
      for (const [k, v] of Object.entries(extra)) p.set(k, v);
      return p;
    },
    [applied, admin, userId, page, pageSize]
  );

  const endpoint = admin ? '/api/shopping-log/admin' : '/api/shopping-log/mine';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await Axios.get(`${api}${endpoint}?${buildParams()}`, auth);
      setData(res);
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setLoading(false);
    }
  }, [auth, buildParams, endpoint]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the admin's member filter in the URL so the page can be shared.
  useEffect(() => {
    if (!admin) return;
    const next = new URLSearchParams();
    if (userId) next.set('userId', userId);
    else if (applied.username) next.set('username', applied.username);
    if (applied.from) next.set('from', applied.from);
    if (applied.to) next.set('to', applied.to);
    if (applied.types.length) next.set('type', applied.types.join(','));
    setUrlQuery(next, { replace: true });
  }, [admin, userId, applied, setUrlQuery]);

  const apply = (next) => {
    const merged = { ...filters, ...(next || {}) };
    setFilters(merged);
    setApplied(merged);
    setPage(1);
    setOpen(null);
  };
  const set = (key) => (e) => setFilters((f) => ({ ...f, [key]: e.target.value }));
  const setNow = (key, value) => apply({ [key]: value });

  const toggleType = (type) => {
    const has = applied.types.includes(type);
    apply({ types: has ? applied.types.filter((t) => t !== type) : [...applied.types, type] });
  };

  const focusUser = (u) => {
    if (!admin || !u?.username) return;
    setUserId(u.id && /^[0-9a-f]{24}$/i.test(u.id) ? u.id : '');
    apply({ username: u.username });
  };
  const clearUser = () => {
    setUserId('');
    apply({ username: '' });
  };
  const clearAll = () => {
    setUserId('');
    setFilters(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setPage(1);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const { data: res } = await Axios.get(`${api}${endpoint}?${buildParams({ export: '1', page: '1' })}`, auth);
      const head = [
        'Date',
        'Time',
        ...(admin ? ['Username', 'Name'] : []),
        'Type',
        'Direction',
        'Amount Tk',
        'Amount pt',
        'Balance after Tk',
        'Balance after (derived) Tk',
        'Details',
        'Reference',
        'Counterparty',
        'By',
        'IP',
        'Location',
        'Via',
        'Source collection',
      ];
      const lines = [head.map(csvCell).join(',')];
      for (const r of res.rows || []) {
        const when = new Date(r.at);
        lines.push(
          [
            isoDay(when),
            when.toLocaleTimeString('en-GB'),
            ...(admin ? [r.user?.username || '', r.user?.name || ''] : []),
            r.typeLabel,
            r.direction,
            r.delta,
            Math.round((r.delta / tkPerPoint) * 100) / 100,
            r.balanceAfter ?? '',
            r.balanceDerived ?? '',
            r.details,
            r.ref?.label || '',
            r.counterparty?.username ? `@${r.counterparty.username}` : '',
            r.actor || '',
            r.ip || '',
            r.location || '',
            r.via || '',
            r.src,
          ]
            .map(csvCell)
            .join(',')
        );
      }
      const blob = new Blob([`﻿${lines.join('\n')}`], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const who = res.user?.username || (admin ? 'all-members' : 'me');
      a.href = url;
      a.download = `shopping-balance-log-${who}-${isoDay(new Date())}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      if ((res.count || 0) > (res.rows || []).length) {
        toast.info(`Exported the first ${fmt(res.rows.length, 0)} of ${fmt(res.count, 0)} rows. Narrow the filters for the rest.`);
      }
    } catch (error) {
      toast.error(getError(error));
    } finally {
      setExporting(false);
    }
  };

  const rows = data?.rows || [];
  const totals = data?.totals || { count: 0, credit: 0, debit: 0, net: 0 };
  const byType = data?.byType || [];
  const typeLabels = data?.typeLabels || {};
  const single = data?.user || null;
  const filtersOn = Boolean(
    applied.q ||
      applied.from ||
      applied.to ||
      applied.types.length ||
      applied.direction ||
      applied.min !== '' ||
      applied.max !== '' ||
      (admin && (applied.username || userId))
  );
  const rangeText = (() => {
    const f = data?.range?.from ? new Date(data.range.from).toLocaleDateString('en-GB') : null;
    const t = data?.range?.to ? new Date(data.range.to).toLocaleDateString('en-GB') : null;
    if (!f && !t) return 'All time';
    return `${f || 'Beginning'} → ${t || 'Now'}`;
  })();

  const balanceCell = (r) => {
    if (r.balanceAfter !== null && r.balanceAfter !== undefined) {
      return (
        <span title="Recorded at the time">
          {tk(r.balanceAfter)}
          <small className="sbl-sub">{pt(r.balanceAfter)}</small>
        </span>
      );
    }
    if (r.balanceDerived !== null && r.balanceDerived !== undefined) {
      return (
        <span className="sbl-derived" title="Worked out from the current balance and every newer movement">
          ≈ {tk(r.balanceDerived)}
          <small className="sbl-sub">derived</small>
        </span>
      );
    }
    return dash;
  };

  const whereCell = (r) =>
    r.ip || r.location ? (
      <span>
        {r.location ? <span className="sbl-loc">{r.location}</span> : null}
        {r.ip ? <small className="sbl-sub sbl-mono">{r.ip}</small> : null}
      </span>
    ) : (
      dash
    );

  const amountCell = (r) => (
    <span className={r.delta >= 0 ? 'sbl-amt sbl-in' : 'sbl-amt sbl-out'}>
      {r.delta > 0 ? '+' : r.delta < 0 ? '−' : ''}
      {tk(r.amountTk)}
      <small className="sbl-sub">≈ {fmt(r.amountPt)} pt</small>
    </span>
  );

  const personLink = (u) => {
    if (!u?.username) return dash;
    return admin ? (
      <button className="sbl-linkbtn" onClick={() => focusUser(u)} title="Show only this member">
        @{u.username}
      </button>
    ) : (
      <span>@{u.username}</span>
    );
  };

  const detailPanel = (r) => (
    <div className="sbl-detail">
      <div className="sbl-kv"><span>Movement</span><b>{r.typeLabel} ({r.direction})</b></div>
      <div className="sbl-kv"><span>Amount</span><b>{r.delta > 0 ? '+' : r.delta < 0 ? '−' : ''}{tk(r.amountTk)} · {fmt(r.amountPt)} pt</b></div>
      <div className="sbl-kv"><span>When</span><b>{new Date(r.at).toLocaleString('en-GB')}</b></div>
      <div className="sbl-kv"><span>Details</span><b>{r.details || '—'}</b></div>
      <div className="sbl-kv"><span>Reference</span><b className="sbl-mono">{r.ref ? `${r.ref.label} (${r.ref.id})` : '—'}</b></div>
      {admin ? (
        <div className="sbl-kv">
          <span>Member</span>
          <b>
            {r.user?.username ? `@${r.user.username}` : '—'} {r.user?.name ? `· ${r.user.name}` : ''}
          </b>
        </div>
      ) : null}
      <div className="sbl-kv">
        <span>Counterparty</span>
        <b>
          {r.counterparty?.username ? `@${r.counterparty.username}` : '—'}
          {r.counterparty?.name ? ` · ${r.counterparty.name}` : ''}
          {r.counterparty?.merchant ? ` · ${r.counterparty.merchant}` : ''}
        </b>
      </div>
      <div className="sbl-kv"><span>Done by</span><b>{r.actor ? `@${r.actor}` : '—'}</b></div>
      <div className="sbl-kv"><span>Balance after (recorded)</span><b>{r.balanceAfter !== null && r.balanceAfter !== undefined ? tk(r.balanceAfter) : '—'}</b></div>
      {r.balanceDerived !== undefined ? (
        <div className="sbl-kv"><span>Balance after (derived)</span><b>{r.balanceDerived !== null ? `≈ ${tk(r.balanceDerived)}` : '—'}</b></div>
      ) : null}
      <div className="sbl-kv"><span>IP address</span><b className="sbl-mono">{r.ip || '—'}</b></div>
      <div className="sbl-kv">
        <span>Location</span>
        <b>
          {r.location || '—'}
          {r.lat !== null && r.lng !== null ? (
            <>
              {' '}
              <a href={`https://maps.google.com/?q=${r.lat},${r.lng}`} target="_blank" rel="noreferrer">
                map
              </a>
            </>
          ) : null}
        </b>
      </div>
      <div className="sbl-kv"><span>Via</span><b>{r.via || '—'}</b></div>
      {r.userAgent ? <div className="sbl-kv"><span>Device</span><b className="sbl-ua">{r.userAgent}</b></div> : null}
      <div className="sbl-kv"><span>Recorded in</span><b className="sbl-mono">{r.src}{r.ledgerStatus ? ` · ${r.ledgerStatus}` : ''}</b></div>
      {admin && r.user?.id ? (
        <div className="sbl-detail-do">
          <Link className="sbl-btn sbl-btn-sm" to={`/admin/userreport/${r.user.id}`}>
            <i className="fa fa-user" aria-hidden="true"></i> Open member
          </Link>
          <button className="sbl-btn sbl-btn-sm" onClick={() => focusUser(r.user)}>
            <i className="fa fa-filter" aria-hidden="true"></i> Only this member
          </button>
        </div>
      ) : null}
    </div>
  );

  const rowKey = (r) => `${r.src}-${r.id}-${r.type}`;

  return (
    <div className={userInfo?.isAdmin ? 'sbl-page sbl-with-sidebar' : 'sbl-page'}>
      <Helmet>
        <title>Shopping Balance Log</title>
      </Helmet>
      <style>{CSS}</style>

      <div className="sbl-head">
        <div className="sbl-head-title">
          {!admin ? (
            <button className="sbl-back" onClick={() => navigate(-1)} aria-label="Back">
              <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
            </button>
          ) : null}
          <div>
            <h3>Shopping Balance Log</h3>
            <div className="sbl-muted">
              {admin
                ? single
                  ? `Every Shopping Balance movement of @${single.username}${single.name ? ` (${single.name})` : ''}.`
                  : 'Every Shopping Balance movement of every member, from every source.'
                : 'Every movement of your Shopping Balance — topups, transfers, orders, refunds and more.'}{' '}
              1 pt = Tk {tkPerPoint}.
            </div>
          </div>
        </div>
        <div className="sbl-head-tools">
          <button className="sbl-btn sbl-btn-light" onClick={load} disabled={loading}>
            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`} aria-hidden="true"></i> Refresh
          </button>
          <button className="sbl-btn sbl-btn-light" onClick={exportCsv} disabled={exporting || !totals.count}>
            <i className="fa fa-download" aria-hidden="true"></i> {exporting ? 'Exporting…' : 'CSV'}
          </button>
        </div>
      </div>

      {admin && single ? (
        <div className="sbl-member">
          <i className="fa fa-user-circle" aria-hidden="true"></i>
          <span>
            Showing <b>@{single.username}</b>
            {single.name ? ` · ${single.name}` : ''}
          </span>
          <Link className="sbl-btn sbl-btn-sm" to={`/admin/userreport/${single.id}`}>Open member</Link>
          <button className="sbl-btn sbl-btn-sm" onClick={clearUser}>Show everyone</button>
        </div>
      ) : null}
      {admin && !single && (data?.matchedUsers || []).length > 1 ? (
        <div className="sbl-hint">
          “{applied.username}” matched {data.matchedUsers.length} members:{' '}
          {data.matchedUsers.slice(0, 12).map((u) => (
            <button key={u.id} className="sbl-chip-btn" onClick={() => focusUser(u)}>
              @{u.username}
            </button>
          ))}
        </div>
      ) : null}

      {/* -------------------------------------------------------- summary */}
      <div className="sbl-stats">
        {data?.currentBalance !== null && data?.currentBalance !== undefined ? (
          <div className="sbl-stat sbl-stat-main">
            <span className="sbl-stat-label">Current balance</span>
            <b>{tk(data.currentBalance)}</b>
            <small>≈ {pt(data.currentBalance)}</small>
          </div>
        ) : null}
        <button
          className={applied.direction === 'credit' ? 'sbl-stat sbl-stat-on' : 'sbl-stat'}
          onClick={() => setNow('direction', applied.direction === 'credit' ? '' : 'credit')}
        >
          <span className="sbl-stat-label">Money in</span>
          <b className="sbl-in">+{tk(totals.credit)}</b>
          <small>≈ {pt(totals.credit)}</small>
        </button>
        <button
          className={applied.direction === 'debit' ? 'sbl-stat sbl-stat-on' : 'sbl-stat'}
          onClick={() => setNow('direction', applied.direction === 'debit' ? '' : 'debit')}
        >
          <span className="sbl-stat-label">Money out</span>
          <b className="sbl-out">−{tk(totals.debit)}</b>
          <small>≈ {pt(totals.debit)}</small>
        </button>
        <div className="sbl-stat sbl-stat-plain">
          <span className="sbl-stat-label">Net</span>
          <b className={totals.net >= 0 ? 'sbl-in' : 'sbl-out'}>
            {totals.net >= 0 ? '+' : '−'}
            {tk(Math.abs(totals.net))}
          </b>
          <small>≈ {pt(Math.abs(totals.net))}</small>
        </div>
        <div className="sbl-stat sbl-stat-plain">
          <span className="sbl-stat-label">Movements</span>
          <b>{fmt(totals.count, 0)}</b>
          <small>{rangeText}</small>
        </div>
      </div>

      {data?.balance && !filtersOn && data.balance.impliedOpening && Math.abs(data.balance.impliedOpening) >= 0.01 ? (
        <div className="sbl-hint">
          <i className="fa fa-info-circle" aria-hidden="true"></i> Tk {fmt(data.balance.impliedOpening)} of the
          balance is older than any recorded movement (or came from a source that kept no record), so “derived”
          balances may differ from the real balance at that time by that amount.
        </div>
      ) : null}

      {/* ----------------------------------------------------- per source */}
      {byType.length ? (
        <div className="sbl-sources">
          {byType.map((t) => (
            <button
              key={t.type}
              className={applied.types.includes(t.type) ? 'sbl-source sbl-source-on' : 'sbl-source'}
              onClick={() => toggleType(t.type)}
              title="Show only this source (click again to remove)"
            >
              <i className={`fa ${TYPE_ICON[t.type] || 'fa-circle-o'}`} aria-hidden="true"></i>
              <span className="sbl-source-name">{t.label}</span>
              <span className="sbl-source-count">{fmt(t.count, 0)}</span>
              <span className={t.net >= 0 ? 'sbl-source-net sbl-in' : 'sbl-source-net sbl-out'}>
                {t.net >= 0 ? '+' : '−'}
                {fmt(Math.abs(t.net))}
              </span>
            </button>
          ))}
        </div>
      ) : null}

      {/* -------------------------------------------------------- filters */}
      <div className="sbl-filters">
        <div className="sbl-filter-row">
          <input
            className="sbl-input sbl-grow"
            placeholder="Search details, username, IP, place"
            value={filters.q}
            onChange={set('q')}
            onKeyDown={(e) => (e.key === 'Enter' ? apply() : null)}
          />
          {admin ? (
            <input
              className="sbl-input"
              placeholder="Member username"
              value={filters.username}
              onChange={(e) => {
                setUserId('');
                set('username')(e);
              }}
              onKeyDown={(e) => (e.key === 'Enter' ? apply() : null)}
            />
          ) : null}
          <button className="sbl-btn sbl-btn-primary" onClick={() => apply()}>
            <i className="fa fa-search" aria-hidden="true"></i> Search
          </button>
          <button
            className={showFilters ? 'sbl-btn sbl-btn-on' : 'sbl-btn'}
            onClick={() => setShowFilters((v) => !v)}
          >
            <i className="fa fa-sliders" aria-hidden="true"></i> Filters
          </button>
          {filtersOn ? (
            <button className="sbl-btn sbl-btn-light" onClick={clearAll}>
              Clear
            </button>
          ) : null}
        </div>

        <div className="sbl-quick">
          {QUICK_RANGES.map((r) => {
            const want = rangeFor(r.key);
            const on = applied.from === want.from && applied.to === want.to;
            return (
              <button key={r.key} className={on ? 'sbl-pill sbl-pill-on' : 'sbl-pill'} onClick={() => apply(want)}>
                {r.label}
              </button>
            );
          })}
          <span className="sbl-seg">
            {[
              ['', 'All'],
              ['credit', 'In'],
              ['debit', 'Out'],
            ].map(([v, l]) => (
              <button
                key={v || 'all'}
                className={applied.direction === v ? 'sbl-seg-on' : ''}
                onClick={() => setNow('direction', v)}
              >
                {l}
              </button>
            ))}
          </span>
          <select className="sbl-input sbl-input-sm" value={applied.sort} onChange={(e) => setNow('sort', e.target.value)}>
            {SORTS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        {showFilters ? (
          <div className="sbl-more">
            <label>
              From
              <input type="date" className="sbl-input" value={filters.from} onChange={set('from')} />
            </label>
            <label>
              To
              <input type="date" className="sbl-input" value={filters.to} onChange={set('to')} />
            </label>
            <label>
              Min amount
              <input type="number" min="0" className="sbl-input" value={filters.min} onChange={set('min')} />
            </label>
            <label>
              Max amount
              <input type="number" min="0" className="sbl-input" value={filters.max} onChange={set('max')} />
            </label>
            <label>
              Amount in
              <select className="sbl-input" value={filters.unit} onChange={set('unit')}>
                <option value="tk">Tk</option>
                <option value="pt">Points</option>
              </select>
            </label>
            <label className="sbl-more-wide">
              Source / type
              <div className="sbl-typepick">
                {Object.entries(typeLabels).map(([key, label]) => (
                  <label key={key} className="sbl-check">
                    <input
                      type="checkbox"
                      checked={filters.types.includes(key)}
                      onChange={() =>
                        setFilters((f) => ({
                          ...f,
                          types: f.types.includes(key) ? f.types.filter((t) => t !== key) : [...f.types, key],
                        }))
                      }
                    />
                    {label}
                  </label>
                ))}
              </div>
            </label>
            <div className="sbl-more-do">
              <button className="sbl-btn sbl-btn-primary" onClick={() => apply()}>
                Apply filters
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {/* ----------------------------------------------------------- rows */}
      {loading && !data ? (
        <div className="sbl-empty">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="sbl-empty">
          <div className="sbl-empty-icon">
            <i className="fa fa-credit-card" aria-hidden="true"></i>
          </div>
          <b>No Shopping Balance movements{filtersOn ? ' match these filters' : ' yet'}.</b>
        </div>
      ) : (
        <>
          <div className={loading ? 'sbl-table-wrap sbl-loading' : 'sbl-table-wrap'}>
            <table className="sbl-table">
              <thead>
                <tr>
                  <th>Date &amp; time</th>
                  {admin ? <th>Member</th> : null}
                  <th>Source</th>
                  <th>Details &amp; reference</th>
                  <th>With</th>
                  <th className="sbl-r">Amount</th>
                  <th className="sbl-r">Balance after</th>
                  <th>IP / location</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const when = fmtDateTime(r.at);
                  const key = rowKey(r);
                  const isOpen = open === key;
                  return (
                    <React.Fragment key={key}>
                      <tr className={isOpen ? 'sbl-row sbl-row-on' : 'sbl-row'} onClick={() => setOpen(isOpen ? null : key)}>
                        <td>
                          <b className="sbl-date">{when.date}</b>
                          <small className="sbl-sub">{when.time}</small>
                        </td>
                        {admin ? (
                          <td onClick={(e) => e.stopPropagation()}>
                            {personLink(r.user)}
                            {r.user?.name ? <small className="sbl-sub">{r.user.name}</small> : null}
                          </td>
                        ) : null}
                        <td>
                          <span className={r.delta >= 0 ? 'sbl-type sbl-type-in' : 'sbl-type sbl-type-out'}>
                            <i className={`fa ${TYPE_ICON[r.type] || 'fa-circle-o'}`} aria-hidden="true"></i> {r.typeLabel}
                          </span>
                        </td>
                        <td className="sbl-details">
                          {r.details || dash}
                          {r.ref ? <small className="sbl-sub sbl-mono">{r.ref.label}</small> : null}
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          {r.counterparty ? personLink(r.counterparty) : dash}
                          {r.actor && r.actor !== r.counterparty?.username ? (
                            <small className="sbl-sub">by @{r.actor}</small>
                          ) : null}
                        </td>
                        <td className="sbl-r">{amountCell(r)}</td>
                        <td className="sbl-r">{balanceCell(r)}</td>
                        <td>{whereCell(r)}</td>
                      </tr>
                      {isOpen ? (
                        <tr className="sbl-row-detail">
                          <td colSpan={admin ? 8 : 7}>{detailPanel(r)}</td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Phone: one card per movement. */}
          <div className={loading ? 'sbl-cards sbl-loading' : 'sbl-cards'}>
            {rows.map((r) => {
              const when = fmtDateTime(r.at);
              const key = rowKey(r);
              const isOpen = open === key;
              return (
                <article key={key} className={isOpen ? 'sbl-card sbl-card-on' : 'sbl-card'}>
                  <div className="sbl-card-top" onClick={() => setOpen(isOpen ? null : key)}>
                    <span className={r.delta >= 0 ? 'sbl-card-ico sbl-ico-in' : 'sbl-card-ico sbl-ico-out'}>
                      <i className={`fa ${TYPE_ICON[r.type] || 'fa-circle-o'}`} aria-hidden="true"></i>
                    </span>
                    <div className="sbl-card-main">
                      <b>{r.typeLabel}</b>
                      <small className="sbl-sub">
                        {when.date} · {when.time}
                      </small>
                      {admin && r.user?.username ? <small className="sbl-sub">@{r.user.username}</small> : null}
                    </div>
                    <div className="sbl-card-amt">{amountCell(r)}</div>
                  </div>
                  <div className="sbl-card-info" onClick={() => setOpen(isOpen ? null : key)}>
                    <span>{r.details || '—'}</span>
                    <span className="sbl-card-meta">
                      Balance: {balanceCell(r)}
                      {r.ip || r.location ? <> · {r.location || r.ip}</> : null}
                    </span>
                  </div>
                  {isOpen ? detailPanel(r) : null}
                </article>
              );
            })}
          </div>

          <div className="sbl-pager">
            <button className="sbl-btn sbl-btn-sm" disabled={page <= 1 || loading} onClick={() => setPage(1)}>
              «
            </button>
            <button className="sbl-btn sbl-btn-sm" disabled={page <= 1 || loading} onClick={() => setPage((p) => p - 1)}>
              ‹ Prev
            </button>
            <span className="sbl-muted">
              Page {data?.page || 1} of {data?.pages || 1} · {fmt(totals.count, 0)} movements
            </span>
            <button
              className="sbl-btn sbl-btn-sm"
              disabled={page >= (data?.pages || 1) || loading}
              onClick={() => setPage((p) => p + 1)}
            >
              Next ›
            </button>
            <select
              className="sbl-input sbl-input-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <p className="sbl-foot sbl-muted">
        Shopping Balance is kept in Tk since 6 Oct 2026; earlier movements are shown as recorded. “Balance after” is
        the balance stored at the time; “≈ derived” is worked out from today's balance. IP and location are shown
        where they were recorded — older movements show “—”.
      </p>
    </div>
  );
}

const CSS = `
.sbl-page { max-width: 1180px; margin: 0 auto; padding: 16px 16px 60px; box-sizing: border-box; }
.sbl-with-sidebar { width: 87%; max-width: none; float: right; padding: 16px 20px 60px; }
@media (max-width: 900px) { .sbl-with-sidebar { width: 100%; float: none; padding: 12px; } }
.sbl-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
.sbl-head h3 { margin: 0 0 2px; }
.sbl-head-title { display: flex; gap: 10px; align-items: flex-start; min-width: 0; }
.sbl-back { border: 0; background: #f3f4f6; border-radius: 10px; width: 36px; height: 36px; cursor: pointer; flex: none; }
.sbl-head-tools { display: flex; gap: 8px; flex-wrap: wrap; }
.sbl-muted { color: #6b7280; font-size: 12.5px; }
.sbl-dash { color: #9ca3af; }
.sbl-sub { display: block; color: #6b7280; font-size: 11.5px; font-weight: 400; }
.sbl-mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.sbl-in { color: #15803d; }
.sbl-out { color: #b91c1c; }

.sbl-btn { border-radius: 9px; padding: 9px 14px; font-weight: 700; cursor: pointer; font-size: 14px;
  border: 1px solid #cfd8e3; background: #fff; text-decoration: none; color: #111827; display: inline-flex;
  align-items: center; gap: 6px; white-space: nowrap; }
.sbl-btn:disabled { opacity: .55; cursor: not-allowed; }
.sbl-btn-sm { padding: 6px 11px; font-size: 12.5px; }
.sbl-btn-primary { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.sbl-btn-light { background: #f3f4f6; }
.sbl-btn-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.sbl-linkbtn { border: 0; background: none; padding: 0; color: #1d6fa5; font-weight: 700; cursor: pointer; }
.sbl-linkbtn:hover { text-decoration: underline; }

.sbl-member, .sbl-hint { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; background: #e6f5fd;
  border: 1px solid #bfe4f8; color: #14506e; border-radius: 10px; padding: 9px 12px; font-size: 13px; margin-top: 12px; }
.sbl-chip-btn { border: 1px solid #bfe4f8; background: #fff; border-radius: 999px; padding: 2px 9px; font-size: 12px; cursor: pointer; }

.sbl-stats { display: grid; grid-template-columns: repeat(auto-fit, minmax(170px, 1fr)); gap: 10px; margin: 14px 0; }
.sbl-stat { text-align: left; background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 12px 14px;
  display: flex; flex-direction: column; gap: 2px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.sbl-stat:hover { border-color: #42b4e7; }
.sbl-stat-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.sbl-stat-plain { cursor: default; }
.sbl-stat-plain:hover { border-color: #e5e7eb; }
.sbl-stat-main { cursor: default; background: linear-gradient(135deg, #14506e, #42b4e7); color: #fff; border: 0; }
.sbl-stat-main .sbl-stat-label, .sbl-stat-main small { color: rgba(255,255,255,.85); }
.sbl-stat-label { font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280; font-weight: 700; }
.sbl-stat b { font-size: 20px; }
.sbl-stat small { color: #6b7280; font-size: 11.5px; }

.sbl-sources { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 12px; }
.sbl-source { display: inline-flex; align-items: center; gap: 7px; border: 1px solid #e5e7eb; background: #fff;
  border-radius: 999px; padding: 6px 12px; font-size: 12.5px; font-weight: 600; color: #374151; cursor: pointer; }
.sbl-source i { color: #9ca3af; }
.sbl-source:hover { border-color: #42b4e7; }
.sbl-source-on { border-color: #42b4e7; background: #e6f5fd; }
.sbl-source-count { background: rgba(0,0,0,.07); border-radius: 999px; padding: 0 7px; font-size: 11px; }
.sbl-source-net { font-size: 11.5px; font-weight: 700; }

.sbl-filters { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px; margin-bottom: 12px;
  display: flex; flex-direction: column; gap: 8px; }
.sbl-filter-row, .sbl-quick { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
.sbl-input { border: 1px solid #cfd8e3; border-radius: 9px; padding: 9px 11px; font-size: 14px; box-sizing: border-box;
  max-width: 100%; background: #fff; }
.sbl-input:focus { outline: 3px solid #cdeafa; border-color: #42b4e7; }
.sbl-input-sm { padding: 6px 9px; font-size: 13px; }
.sbl-grow { flex: 1 1 220px; }
.sbl-pill { border: 1px solid #e5e7eb; background: #f9fafb; border-radius: 999px; padding: 5px 12px; font-size: 12.5px;
  font-weight: 600; cursor: pointer; color: #374151; }
.sbl-pill-on { background: #42b4e7; border-color: #42b4e7; color: #fff; }
.sbl-seg { display: inline-flex; background: #eef2f7; border-radius: 999px; padding: 3px; gap: 2px; }
.sbl-seg button { border: 0; background: none; border-radius: 999px; padding: 4px 12px; font-size: 12.5px; font-weight: 700;
  color: #475569; cursor: pointer; }
.sbl-seg .sbl-seg-on { background: #fff; color: #14506e; box-shadow: 0 1px 3px rgba(0,0,0,.12); }
.sbl-more { display: grid; grid-template-columns: repeat(auto-fit, minmax(150px, 1fr)); gap: 10px; border-top: 1px solid #f3f4f6;
  padding-top: 10px; }
.sbl-more label { display: flex; flex-direction: column; gap: 4px; font-size: 11.5px; font-weight: 700; color: #6b7280;
  text-transform: uppercase; letter-spacing: .03em; margin: 0; }
.sbl-more-wide { grid-column: 1 / -1; }
.sbl-typepick { display: flex; flex-wrap: wrap; gap: 6px 14px; text-transform: none; letter-spacing: 0; }
.sbl-typepick .sbl-check { flex-direction: row; align-items: center; gap: 6px; font-size: 13px; font-weight: 500;
  color: #374151; text-transform: none; }
.sbl-more-do { grid-column: 1 / -1; display: flex; justify-content: flex-end; }

.sbl-table-wrap { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; overflow-x: auto; }
.sbl-loading { opacity: .6; }
.sbl-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.sbl-table th { text-align: left; font-size: 11px; text-transform: uppercase; letter-spacing: .04em; color: #6b7280;
  background: #f9fafb; padding: 9px 10px; border-bottom: 1px solid #e5e7eb; white-space: nowrap; }
.sbl-table td { padding: 9px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
.sbl-row { cursor: pointer; }
.sbl-row:hover td { background: #f8fbfd; }
.sbl-row-on td { background: #eef8fd; }
.sbl-row-detail td { background: #f8fafc; }
.sbl-r { text-align: right; white-space: nowrap; }
.sbl-date { white-space: nowrap; }
.sbl-details { max-width: 340px; overflow-wrap: anywhere; }
.sbl-type { display: inline-flex; align-items: center; gap: 5px; border-radius: 999px; padding: 2px 9px; font-size: 11.5px;
  font-weight: 700; white-space: nowrap; }
.sbl-type-in { background: #dcfce7; color: #166534; }
.sbl-type-out { background: #fee2e2; color: #991b1b; }
.sbl-amt { font-weight: 800; font-size: 14px; }
.sbl-derived { color: #475569; }
.sbl-loc { font-weight: 600; }

.sbl-detail { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 0 18px; padding: 4px 2px; }
.sbl-kv { display: flex; justify-content: space-between; gap: 12px; font-size: 12.5px; border-bottom: 1px solid #eef0f3;
  padding: 5px 0; }
.sbl-kv span { color: #6b7280; flex: none; }
.sbl-kv b { text-align: right; overflow-wrap: anywhere; font-weight: 600; }
.sbl-ua { font-size: 11px; font-weight: 400 !important; }
.sbl-detail-do { grid-column: 1 / -1; display: flex; gap: 8px; flex-wrap: wrap; padding-top: 8px; }

.sbl-cards { display: none; flex-direction: column; gap: 8px; }
.sbl-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 10px 12px; }
.sbl-card-on { border-color: #42b4e7; box-shadow: 0 0 0 3px #e6f5fd; }
.sbl-card-top { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.sbl-card-ico { flex: none; width: 36px; height: 36px; border-radius: 10px; display: flex; align-items: center;
  justify-content: center; }
.sbl-ico-in { background: #dcfce7; color: #166534; }
.sbl-ico-out { background: #fee2e2; color: #991b1b; }
.sbl-card-main { flex: 1 1 auto; min-width: 0; }
.sbl-card-amt { text-align: right; flex: none; }
.sbl-card-info { font-size: 12.5px; color: #374151; margin-top: 6px; display: flex; flex-direction: column; gap: 2px;
  overflow-wrap: anywhere; cursor: pointer; }
.sbl-card-meta { color: #6b7280; font-size: 11.5px; }
.sbl-card-meta .sbl-sub { display: inline; }
.sbl-card .sbl-detail { margin-top: 8px; border-top: 1px solid #f3f4f6; padding-top: 6px; }

@media (max-width: 760px) {
  .sbl-table-wrap { display: none; }
  .sbl-cards { display: flex; }
  .sbl-stat b { font-size: 17px; }
}

.sbl-empty { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: 32px 16px; text-align: center; }
.sbl-empty-icon { font-size: 30px; color: #b6d7e8; margin-bottom: 6px; }
.sbl-pager { display: flex; align-items: center; justify-content: center; gap: 8px; padding: 12px 0; flex-wrap: wrap; }
.sbl-foot { margin-top: 10px; }
`;
